import { useId } from 'react';
import { ELEMENT_CATEGORIES, MODEL_ELEMENT_CATEGORIES, type ElementCategory } from '@scoutaround/script-breakdown-shared';

import { CategoryIcon } from './CategoryIcon.js';

import '../../styles/elements.css';

/**
 * WHICH ELEMENTS THE AI READING LOOKS FOR (2026-11-05): the seventeen breakdown categories, each a
 * checkbox. The ticks start from the SUGGESTION — what a first look at the text finds signs of
 * (`GET …/element-recommendation`: the skill routing's term lists and headings, free, no model) —
 * and every suggested category stays marked "Suggested" whatever the producer changes, with "Use
 * suggestion" to go back to it. The ticked list is what the reading asks, prices and charges for
 * (`ElementCategoryChoice`, shared).
 *
 * The state is the caller's (`AnalyseWithAi`), which locks the picker while the suggestion loads,
 * an estimate is open or a reading is running, so what is approved is what is ticked.
 */
export function ElementCategoryPicker({
  chosen,
  suggested,
  fallback,
  state,
  onChange,
  onUseSuggestion,
  disabled,
  testIdPrefix,
}: {
  chosen: ReadonlySet<ElementCategory>;
  /** The suggestion; null while it loads or when none could be made. */
  suggested: ReadonlySet<ElementCategory> | null;
  /** The text gave too little to go on, so every category is suggested. */
  fallback: boolean;
  state: 'loading' | 'ready' | 'unavailable';
  onChange: (next: ReadonlySet<ElementCategory>) => void;
  onUseSuggestion: () => void;
  disabled: boolean;
  testIdPrefix: string;
}) {
  const introId = useId();
  const noteId = useId();
  const total = MODEL_ELEMENT_CATEGORIES.length;
  const asSuggested =
    suggested !== null && suggested.size === chosen.size && [...suggested].every((category) => chosen.has(category));

  const toggle = (category: ElementCategory) => {
    const next = new Set(chosen);
    if (next.has(category)) next.delete(category);
    else next.add(category);
    onChange(next);
  };

  const intro =
    state === 'loading'
      ? 'Checking the text for signs of each category…'
      : state === 'unavailable'
        ? 'No suggestion could be made, so every category is ticked. Untick the ones you do not need.'
        : fallback
          ? 'The text gives too little to go on, so every category is suggested. Untick the ones you do not need.'
          : 'Suggested from a first look at the text, which uses no AI and costs nothing. Change any of them.';

  /** What the ticks mean for this reading, in one sentence; nothing while every category is ticked. */
  const note =
    state === 'loading'
      ? null
      : chosen.size === 0
        ? 'Tick at least one category to read.'
        : chosen.size < total
          ? `Only the ${chosen.size === 1 ? 'category' : `${String(chosen.size)} categories`} ticked ${chosen.size === 1 ? 'is' : 'are'} read and charged for. What the others already have stays as it is.${chosen.has('CAST') ? '' : ' Speaking parts are still read from the character names, without AI.'}`
          : null;

  return (
    <fieldset
      className="el-catpick"
      disabled={disabled}
      aria-busy={state === 'loading'}
      aria-describedby={note === null ? introId : `${introId} ${noteId}`}
      data-testid={`${testIdPrefix}-categories`}
      data-state={state}
    >
      <legend className="el-catpick-legend">
        <span className="colh">Elements to read</span>
        <span className="el-catpick-count" data-testid={`${testIdPrefix}-categories-count`}>
          {state === 'loading' ? '' : `${String(chosen.size)} of ${String(total)}`}
        </span>
      </legend>

      <p id={introId} className="el-catpick-intro" data-testid={`${testIdPrefix}-categories-intro`}>
        {intro}
      </p>

      <div className="el-catpick-bulk">
        {suggested !== null && !asSuggested ? (
          <button
            type="button"
            className="el-link"
            disabled={disabled}
            data-testid={`${testIdPrefix}-categories-suggested`}
            onClick={onUseSuggestion}
          >
            Use suggestion
          </button>
        ) : null}
        <button
          type="button"
          className="el-link"
          disabled={disabled || chosen.size === total}
          data-testid={`${testIdPrefix}-categories-all`}
          onClick={() => {
            onChange(new Set(MODEL_ELEMENT_CATEGORIES));
          }}
        >
          Select all
        </button>
        <button
          type="button"
          className="el-link"
          disabled={disabled || chosen.size === 0}
          data-testid={`${testIdPrefix}-categories-none`}
          onClick={() => {
            onChange(new Set());
          }}
        >
          Clear
        </button>
      </div>

      <ul className="el-catpick-grid">
        {MODEL_ELEMENT_CATEGORIES.map((category) => {
          const isSuggested = suggested?.has(category) === true && !fallback;
          return (
            <li key={category}>
              <label className="el-catpick-item" data-on={chosen.has(category) ? '1' : '0'}>
                <input
                  type="checkbox"
                  checked={chosen.has(category)}
                  onChange={() => {
                    toggle(category);
                  }}
                  data-testid={`${testIdPrefix}-category-${category}`}
                />
                <CategoryIcon category={category} size={22} />
                <span className="el-catpick-label">
                  {ELEMENT_CATEGORIES[category].label}
                  {isSuggested ? (
                    <span className="el-catpick-suggested" data-testid={`${testIdPrefix}-category-${category}-suggested`}>
                      Suggested
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {note === null ? null : (
        <p
          id={noteId}
          className="el-catpick-note"
          data-tone={chosen.size === 0 ? 'warn' : undefined}
          data-testid={`${testIdPrefix}-categories-note`}
        >
          {note}
        </p>
      )}
    </fieldset>
  );
}
